import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Switch,
  FormControlLabel,
  Collapse,
  InputAdornment,
} from "@mui/material";
import {
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import Header from "../Header";
import io from "socket.io-client";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";
const BUZZER_ID = 1;

const defaultConfig = {
  buzzer_name: "Default Buzzer",
  description: "",
  countdown_seconds: 10,
  pre_countdown_seconds: 3,
  auto_reset_delay_seconds: 5,
  is_active: false,
  target_mode: "all",
  locked_color: "#424242",
  active_color: "#00c853",
  buzzed_color: "#ffb300",
  timed_out_color: "#d50000",
  locked_audio_url: "",
  active_audio_url: "",
  buzzed_audio_url: "",
  timed_out_audio_url: "",
  locked_image_url: "",
  active_image_url: "",
  buzzed_image_url: "",
  timed_out_image_url: "",
};

const COUNTDOWN_MIN = 3;
const COUNTDOWN_MAX = 60;
const PRE_COUNTDOWN_MIN = 1;
const PRE_COUNTDOWN_MAX = 10;
const AUTO_RESET_MIN = 0;
const AUTO_RESET_MAX = 120;

const BuzzerDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [configLoadError, setConfigLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [config, setConfig] = useState({ ...defaultConfig });
  const [colorsOpen, setColorsOpen] = useState(false);
  const [audioOpen, setAudioOpen] = useState(false);
  const [imagesOpen, setImagesOpen] = useState(false);
  const [audioUploadingKey, setAudioUploadingKey] = useState(null);

  const getAuthToken = () => localStorage.getItem("auth_token");

  const handleChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setSaveError(null);
  };

  const clampNumber = (value, min, max) => {
    const n = Number(value);
    if (Number.isNaN(n)) return min;
    return Math.min(max, Math.max(min, n));
  };

  const handleAudioUpload = async (field, event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    try {
      setAudioUploadingKey(field);
      setSaveError(null);

      const formData = new FormData();
      formData.append("audio", file);

      const res = await fetch(`${API_BASE}/api/upload/buzzer-audio`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: formData,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setSaveError(data.error || "Failed to upload audio");
        return;
      }

      if (data.audioUrl) {
        handleChange(field, data.audioUrl);
      }
    } catch (err) {
      console.error("Buzzer audio upload error", err);
      setSaveError("Failed to upload audio");
    } finally {
      setAudioUploadingKey(null);
      // reset the input so the same file can be selected again if needed
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setConfigLoadError(null);
      const res = await fetch(`${API_BASE}/api/buzzer/${BUZZER_ID}/config`, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setConfigLoadError(data.error || "Failed to load config");
        return;
      }
      if (data.success && data.config) {
        setConfig({
          ...defaultConfig,
          ...data.config,
          is_active: Boolean(data.config.is_active),
        });
      }
    } catch (err) {
      console.error("Buzzer config fetch error", err);
      setConfigLoadError("Failed to load config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const saveConfig = async () => {
    const name = (config.buzzer_name || "").trim();
    if (!name) {
      setSaveError("Buzzer name is required");
      return;
    }
    const countdown = clampNumber(config.countdown_seconds, COUNTDOWN_MIN, COUNTDOWN_MAX);
    const preCountdown = clampNumber(config.pre_countdown_seconds, PRE_COUNTDOWN_MIN, PRE_COUNTDOWN_MAX);
    const autoReset = clampNumber(config.auto_reset_delay_seconds, AUTO_RESET_MIN, AUTO_RESET_MAX);

    try {
      setSaveLoading(true);
      setSaveError(null);
      const res = await fetch(`${API_BASE}/api/buzzer/${BUZZER_ID}/config`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          buzzer_name: name,
          description: config.description ?? "",
          countdown_seconds: countdown,
          pre_countdown_seconds: preCountdown,
          auto_reset_delay_seconds: autoReset,
          target_mode: config.target_mode || "all",
          locked_color: config.locked_color || undefined,
          active_color: config.active_color || undefined,
          buzzed_color: config.buzzed_color || undefined,
          timed_out_color: config.timed_out_color || undefined,
          locked_audio_url: config.locked_audio_url || undefined,
          active_audio_url: config.active_audio_url || undefined,
          buzzed_audio_url: config.buzzed_audio_url || undefined,
          timed_out_audio_url: config.timed_out_audio_url || undefined,
          locked_image_url: config.locked_image_url || undefined,
          active_image_url: config.active_image_url || undefined,
          buzzed_image_url: config.buzzed_image_url || undefined,
          timed_out_image_url: config.timed_out_image_url || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(data.error || "Failed to save config");
        return;
      }
      if (data.success && data.config) {
        setConfig((prev) => ({
          ...prev,
          ...data.config,
          is_active: prev.is_active,
        }));
      }
    } catch (err) {
      console.error("Buzzer config save error", err);
      setSaveError("Failed to save config");
    } finally {
      setSaveLoading(false);
    }
  };

  const activateBuzzer = async () => {
    try {
      setLoading(true);
      await fetch(`${API_BASE}/api/buzzer/${BUZZER_ID}/activate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          target_mode: config.target_mode,
        }),
      });
      setConfig((prev) => ({ ...prev, is_active: true }));
    } catch (err) {
      console.error("Buzzer activate error", err);
    } finally {
      setLoading(false);
    }
  };

  const deactivateBuzzer = async () => {
    try {
      setLoading(true);
      await fetch(`${API_BASE}/api/buzzer/${BUZZER_ID}/deactivate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });
      setConfig((prev) => ({ ...prev, is_active: false }));
    } catch (err) {
      console.error("Buzzer deactivate error", err);
    } finally {
      setLoading(false);
    }
  };

  const [currentRound, setCurrentRound] = useState(null);
  const [lastWinner, setLastWinner] = useState(null);
  const socketRef = useRef(null);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ["websocket"] });
    socketRef.current = socket;

    socket.on("buzzer.activated", (payload) => {
      setCurrentRound({
        round_id: payload.round_id,
        buzzer_id: payload.buzzer_id,
        target_mode: payload.target_mode,
        status: "active",
      });
      setLastWinner(null);
    });

    socket.on("buzzer.buzzed", (payload) => {
      setCurrentRound((prev) =>
        prev
          ? { ...prev, status: "buzzed", round_id: payload.round_id }
          : { status: "buzzed", round_id: payload.round_id },
      );
      setLastWinner({
        name: payload.winner?.display_name || "Winner",
        device_id: payload.winner?.device_id,
        latency_ms: payload.latency_ms ?? null,
      });
    });

    socket.on("buzzer.timeout", () => {
      setCurrentRound((prev) =>
        prev ? { ...prev, status: "timed_out" } : null,
      );
    });

    socket.on("buzzer.locked", () => {
      setCurrentRound(null);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, pb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h4">Buzzer</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              disabled={loading}
              onClick={fetchConfig}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saveLoading || loading}
              onClick={saveConfig}
            >
              Save config
            </Button>
          </Box>
        </Box>

        {configLoadError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {configLoadError}
          </Typography>
        )}
        {saveError && (
          <Typography color="error" sx={{ mb: 2 }}>
            {saveError}
          </Typography>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, bgcolor: "#1E1E1E" }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Basic information
              </Typography>
              <TextField
                fullWidth
                required
                label="Buzzer Name"
                value={config.buzzer_name ?? ""}
                onChange={(e) => handleChange("buzzer_name", e.target.value)}
                error={!!(saveError && !(config.buzzer_name || "").trim())}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Description"
                value={config.description ?? ""}
                onChange={(e) => handleChange("description", e.target.value)}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Switch
                    checked={Boolean(config.is_active)}
                    onChange={(e) =>
                      e.target.checked ? activateBuzzer() : deactivateBuzzer()
                    }
                  />
                }
                label={config.is_active ? "Active" : "Locked"}
              />
            </Paper>

            <Paper sx={{ p: 3, bgcolor: "#1E1E1E", mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Timing
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Pre-countdown (s)"
                    type="number"
                    inputProps={{ min: PRE_COUNTDOWN_MIN, max: PRE_COUNTDOWN_MAX }}
                    value={config.pre_countdown_seconds ?? 3}
                    onChange={(e) =>
                      handleChange(
                        "pre_countdown_seconds",
                        parseInt(e.target.value || "0", 10),
                      )
                    }
                    helperText={`${PRE_COUNTDOWN_MIN}-${PRE_COUNTDOWN_MAX}`}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Active time (s)"
                    type="number"
                    inputProps={{ min: COUNTDOWN_MIN, max: COUNTDOWN_MAX }}
                    value={config.countdown_seconds ?? 10}
                    onChange={(e) =>
                      handleChange(
                        "countdown_seconds",
                        parseInt(e.target.value || "0", 10),
                      )
                    }
                    helperText={`${COUNTDOWN_MIN}-${COUNTDOWN_MAX}`}
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Auto-reset (s)"
                    type="number"
                    inputProps={{ min: AUTO_RESET_MIN, max: AUTO_RESET_MAX }}
                    value={config.auto_reset_delay_seconds ?? 5}
                    onChange={(e) =>
                      handleChange(
                        "auto_reset_delay_seconds",
                        parseInt(e.target.value || "0", 10),
                      )
                    }
                    helperText={`${AUTO_RESET_MIN}-${AUTO_RESET_MAX}`}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Target Mode</InputLabel>
                  <Select
                    value={config.target_mode ?? "all"}
                    label="Target Mode"
                    onChange={(e) =>
                      handleChange("target_mode", e.target.value)
                    }
                  >
                    <MenuItem value="all">All Users</MenuItem>
                    <MenuItem value="force_all">Force All</MenuItem>
                    <MenuItem value="force_selected" disabled>
                      Force Selected (later)
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: "#1E1E1E", mt: 3 }}>
              <Button
                fullWidth
                onClick={() => setColorsOpen((o) => !o)}
                endIcon={colorsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ justifyContent: "space-between" }}
              >
                <Typography variant="h6">Color schemes</Typography>
              </Button>
              <Collapse in={colorsOpen}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {[
                    { key: "locked_color", label: "Locked" },
                    { key: "active_color", label: "Active" },
                    { key: "buzzed_color", label: "Buzzed" },
                    { key: "timed_out_color", label: "Timed out" },
                  ].map(({ key, label }) => (
                    <Grid item xs={12} sm={6} key={key}>
                      <TextField
                        fullWidth
                        size="small"
                        label={label}
                        type="color"
                        value={config[key] || "#000000"}
                        onChange={(e) => handleChange(key, e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: 1,
                                  bgcolor: config[key] || "#000",
                                  border: "1px solid #555",
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{
                          style: { height: 40 },
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: "#1E1E1E", mt: 3 }}>
              <Button
                fullWidth
                onClick={() => setAudioOpen((o) => !o)}
                endIcon={audioOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ justifyContent: "space-between" }}
              >
                <Typography variant="h6">Audio (URLs)</Typography>
              </Button>
              <Collapse in={audioOpen}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {[
                    { key: "locked_audio_url", label: "Lock sound (any → Locked)" },
                    { key: "active_audio_url", label: "Activation (Locked → Active)" },
                    { key: "buzzed_audio_url", label: "Correct buzz (Active → Buzzed)" },
                    { key: "timed_out_audio_url", label: "Timeout (Active → Timed out)" },
                  ].map(({ key, label }) => (
                    <Grid item xs={12} key={key}>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <TextField
                          fullWidth
                          size="small"
                          label={label}
                          value={config[key] ?? ""}
                          onChange={(e) => handleChange(key, e.target.value)}
                          placeholder="https://..."
                        />
                        <Button
                          variant="outlined"
                          component="label"
                          disabled={!!audioUploadingKey}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {audioUploadingKey === key ? "Uploading..." : "Upload"}
                          <input
                            type="file"
                            accept="audio/*"
                            hidden
                            onChange={(e) => handleAudioUpload(key, e)}
                          />
                        </Button>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Paper>

            <Paper sx={{ p: 3, bgcolor: "#1E1E1E", mt: 3 }}>
              <Button
                fullWidth
                onClick={() => setImagesOpen((o) => !o)}
                endIcon={imagesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ justifyContent: "space-between" }}
              >
                <Typography variant="h6">Images / icons (URLs)</Typography>
              </Button>
              <Collapse in={imagesOpen}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {[
                    { key: "locked_image_url", label: "Locked" },
                    { key: "active_image_url", label: "Active" },
                    { key: "buzzed_image_url", label: "Buzzed" },
                    { key: "timed_out_image_url", label: "Timed out" },
                  ].map(({ key, label }) => (
                    <Grid item xs={12} key={key}>
                      <TextField
                        fullWidth
                        size="small"
                        label={`Image – ${label}`}
                        value={config[key] ?? ""}
                        onChange={(e) => handleChange(key, e.target.value)}
                        placeholder="https://..."
                      />
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, bgcolor: "#1E1E1E" }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Live round controls
              </Typography>
              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PlayIcon />}
                  disabled={loading || config.is_active}
                  onClick={activateBuzzer}
                >
                  Activate buzzer
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<StopIcon />}
                  disabled={loading || !config.is_active}
                  onClick={deactivateBuzzer}
                >
                  Lock buzzer
                </Button>
              </Box>
              {currentRound ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Current round: #{currentRound.round_id} (
                    {currentRound.status})
                  </Typography>
                  {lastWinner && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      Winner: {lastWinner.name}
                      {typeof lastWinner.latency_ms === "number" &&
                        ` – ${Math.round(lastWinner.latency_ms)} ms`}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No active round. Activate the buzzer to start a new one.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default BuzzerDashboard;
